/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Alert, Spin } from 'antd'
import BaseModal from 'src/components/ts-components/BaseModal'
import colors from '@sportninja/common/constants/appColors'
import type { VerifyPreview } from '@sportninja/common/services/teamBalance'
import Price from 'src/components/ts-components/Price'
import { dollarsToMinor } from '../utils/balanceMath'
import {
  balanceLabel,
  balanceText,
  billingTypeLabel,
} from '../utils/balanceI18n'

type VerifyReviewModalProps = {
  open: boolean
  loading: boolean
  preview: VerifyPreview | null
  error: string | null
  currencyCode: string
  confirming: boolean
  onConfirm: (settlementFingerprint: string) => void
  onClose: () => void
}

/**
 * SN-4709 D39 — the admin's re-review before verifying, and the ONLY place a
 * drifted payment can be settled.
 *
 * Two drift classes with deliberately different affordances:
 *
 *  - `settlement_plan_changed` — same payer, same money, but the bills it would
 *    settle moved (an online payment landed, the slot locked, a bill re-split).
 *    Confirmable: the admin sees the refreshed plan and explicitly confirms the
 *    CURRENT fingerprint, so they are agreeing to what will actually happen
 *    rather than to what was true when the cash was recorded.
 *
 *  - `settlement_target_changed` — the payer, subject, slot, roster or currency
 *    changed. NOT confirmable, and this modal must never render a confirm button
 *    for it. There is no contract for re-denominating or re-attributing someone's
 *    money; the only honest recovery is to void and record it again against the
 *    truth. The server rejects it regardless — this simply refuses to offer an
 *    action that cannot succeed.
 */
const VerifyReviewModal = ({
  open,
  loading,
  preview,
  error,
  currencyCode,
  confirming,
  onConfirm,
  onClose,
}: VerifyReviewModalProps) => {
  const targetChanged = preview?.drift_class === 'settlement_target_changed'
  const planChanged = preview?.drift_class === 'settlement_plan_changed'
  const canConfirm =
    !loading && !confirming && preview !== null && !targetChanged

  return (
    <BaseModal
      isOpen={open}
      title={balanceLabel('common:balanceVerifyTitle')}
      okText={
        planChanged
          ? balanceLabel('common:balanceVerifyConfirm')
          : balanceLabel('common:balanceVerify')
      }
      cancelText={balanceLabel('common:balanceCommsCancel')}
      disableOk={!canConfirm}
      isLoading={confirming}
      onOk={() => preview && onConfirm(preview.settlement_fingerprint)}
      onClose={onClose}
      desktopWidth='560px'
      /**
       * U2 / SN-8449 — this modal is opened from INSIDE the payments drawer, so it
       * has to clear the drawer wherever the sheet is: 1030 on the Team page, one
       * layer above the host modal when the sheet is mounted in the admin
       * Registration Details modal (10002). A plain BaseModal paints at mask
       * 10001 / wrapper 10002 — level with that drawer — so it steps up one level.
       *
       * (This used to pass `zIndex={zIndex.modal}`, which never moved the modal:
       * BaseModal paints through `styles.wrapper`, which beats antd's `zIndex`
       * prop. All the prop did was pin the modal's own popups to 1140, under it.)
       */
      elevated
      destroyOnClose
    >
      {loading ? (
        <Spin data-testid='verify-preview-loading' />
      ) : error ? (
        <Alert
          type='error'
          showIcon
          message={error}
          data-testid='verify-preview-error'
        />
      ) : preview ? (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
          data-testid='verify-preview'
        >
          {targetChanged ? (
            <Alert
              type='error'
              showIcon
              data-testid='verify-target-changed'
              message={balanceText('common:balanceVerifyTargetChanged')}
            />
          ) : planChanged ? (
            <Alert
              type='warning'
              showIcon
              data-testid='verify-plan-drift'
              message={balanceText('common:balanceVerifyDrift')}
            />
          ) : (
            <span
              css={css`
                color: ${colors.NEUTRAL_70};
              `}
            >
              {balanceText('common:balanceVerifyIntro')}
            </span>
          )}

          {/*
           * A1 — what the rep wrote down when they took the money. This is the
           * screen where an admin decides whether the money was really received,
           * and it used to show neither: no reference to check against the cheque
           * or terminal slip, no note explaining the circumstances. Both are
           * admin-only here; only the reference reaches the player's receipt.
           */}
          {preview.reference || preview.note ? (
            <div
              data-testid='verify-preview-provenance'
              css={css`
                display: flex;
                flex-direction: column;
                gap: 2px;
                font-size: 13px;
                color: ${colors.NEUTRAL_70};
                overflow-wrap: anywhere;
              `}
            >
              {preview.reference ? (
                <span data-testid='verify-preview-reference'>
                  {balanceLabel('common:balanceOfflineReference')}:{' '}
                  {preview.reference}
                </span>
              ) : null}
              {preview.note ? (
                <span data-testid='verify-preview-note'>
                  {balanceLabel('common:balanceOfflineNote')}: {preview.note}
                </span>
              ) : null}
            </div>
          ) : null}

          {preview.unavailable_bill_uids.length > 0 ? (
            <Alert
              type='info'
              showIcon
              data-testid='verify-unavailable-bills'
              message={balanceText('common:balanceVerifyUnavailableBills')}
            />
          ) : null}

          {/* The refreshed plan: what verifying RIGHT NOW would settle. */}
          <div data-testid='verify-preview-rows'>
            {preview.applications.map((application) => (
              <div
                key={application.bill_uid}
                css={css`
                  display: flex;
                  justify-content: space-between;
                  gap: 8px;
                  padding: 4px 0;
                `}
              >
                <span>{billingTypeLabel(application.bill_type)}</span>
                <Price
                  amount={dollarsToMinor(application.applied_total)}
                  currency={currencyCode}
                />
              </div>
            ))}
          </div>

          {preview.surplus > 0 ? (
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                gap: 8px;
              `}
              data-testid='verify-preview-surplus'
            >
              <span>{balanceLabel('common:balanceOfflineSurplus')}</span>
              <Price
                amount={dollarsToMinor(preview.surplus)}
                currency={currencyCode}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </BaseModal>
  )
}

export default VerifyReviewModal
